/**
 * Mock data for the Estate Planning data layer. No live Session/Inventory
 * Service exists yet — every value here is shaped to match the API examples
 * in ESTATE_SESSION_LLD.md / ESTATE_INVENTORY_LLD.md exactly, so hooks can be
 * swapped from mock to fetchRest() without changing consumer-facing shapes.
 *
 * Session and inventory "documents" are kept as module-level mutable state so
 * that create/edit/delete/import-sync/save-progress mutations behave
 * statefully within a dev session (persisted only for the tab's lifetime).
 */
import {createEstateEntityId} from './createEstateEntityId';
import type {
  CashAccountDetails,
  EstateAnswersBySection,
  EstateDocumentsResponse,
  EstateSession,
  EstateSessionListItem,
  EstatePrefillResponse,
  InventoryLineItem,
  InventoryLineItemsResponse,
} from './types';
import {buildTrustPrefillAnswers, buildWillPrefillAnswers} from './mockPrefillFixtures';
import {getMockComposedFlowTemplate} from './getMockComposedFlowTemplate';

// ---------------------------------------------------------------------------
// Session Service mocks (ESTATE_SESSION_LLD.md)
// ---------------------------------------------------------------------------

/** Default Will-package dev session id. */
export const MOCK_SESSION_ID = '68d1f2a0b3c8e9f0a1b2c3d4';

/** Trust-package dev session id (also appears in MOCK_SESSION_LIST). */
export const MOCK_SESSION_ID_TRUST = '507f1f77bcf86cd799439011';

function createInitialSession(id: string, packageType: EstateSession['packageType']): EstateSession {
  return {
    id,
    status: 'ACTIVE',
    state: 'TX',
    packageType,
    questionnaireCatalogVersion: '2026.07.01',
    progress: {
      lastCompletedSectionId: null,
      completedSectionIds: [],
      questionnaireComplete: false,
      questionnaireCompletedAt: null,
    },
    jobDetails: null,
    version: 7,
    createdAt: '2026-07-08T07:10:00Z',
    updatedAt: '2026-07-08T07:10:00Z',
  };
}

const initialWillSession = createInitialSession(MOCK_SESSION_ID, 'WILL');
const initialTrustSession = createInitialSession(MOCK_SESSION_ID_TRUST, 'TRUST');

/** In-memory session documents keyed by session id — mutated by useSaveProgress. */
let mockSessionStores: Record<string, EstateSession> = {
  [MOCK_SESSION_ID]: {...initialWillSession},
  [MOCK_SESSION_ID_TRUST]: {...initialTrustSession},
};

/** In-memory saved answers keyed by session id — mutated by useSaveProgress. */
let mockAnswersStores: Record<string, EstateAnswersBySection> = {
  [MOCK_SESSION_ID]: {},
  [MOCK_SESSION_ID_TRUST]: {},
};

export function getMockSession(sessionId: string = MOCK_SESSION_ID): EstateSession {
  return mockSessionStores[sessionId] ?? mockSessionStores[MOCK_SESSION_ID];
}

/** Live in-memory session documents — list mocks read this so `setMockSession` is visible to bootstrap. */
export function getMockSessions(): EstateSession[] {
  return Object.values(mockSessionStores).map((session) => ({...session}));
}

export function getMockAnswers(sessionId: string = MOCK_SESSION_ID): EstateAnswersBySection {
  return mockAnswersStores[sessionId] ?? mockAnswersStores[MOCK_SESSION_ID];
}

function toSessionListItem(session: EstateSession): EstateSessionListItem {
  return {
    id: session.id,
    status: session.status,
    state: session.state,
    packageType: session.packageType,
    questionnaireCatalogVersion: session.questionnaireCatalogVersion,
    updatedAt: session.updatedAt,
    lastUpdatedAt: session.updatedAt,
  };
}

/** Live mock session list derived from the in-memory store (includes abandoned rows). */
export function getMockSessionList(): EstateSessionListItem[] {
  return Object.values(mockSessionStores).map(toSessionListItem);
}

/** The current `ACTIVE` mock session for a package, if one exists. */
export function getActiveMockSession(packageType: EstateSession['packageType'] = 'WILL'): EstateSession | undefined {
  return Object.values(mockSessionStores).find(
    (session) => session.status === 'ACTIVE' && session.packageType === packageType
  );
}

/** Marks a mock session `ABANDONED` so resume-or-create will not pick it. */
export function abandonMockEstateSession(sessionId: string): EstateSession {
  const storeKey = mockSessionStores[sessionId] ? sessionId : MOCK_SESSION_ID;
  const session = mockSessionStores[storeKey];
  mockSessionStores[storeKey] = {
    ...session,
    status: 'ABANDONED',
    updatedAt: new Date().toISOString(),
  };
  return mockSessionStores[storeKey];
}

/**
 * Inserts a new empty `ACTIVE` mock session (used after abandon, or mock `POST /estate/sessions`).
 * Returns the new session id.
 */
export function insertMockEstateSession(packageType: EstateSession['packageType'], state?: string): string {
  const id = createEstateEntityId();
  const session = createInitialSession(id, packageType);
  mockSessionStores[id] = state && state.length === 2 ? {...session, state} : session;
  mockAnswersStores[id] = {};
  return id;
}

/** Test/dev helper: seed saved answers without bumping session version. */
export function seedMockAnswers(sessionId: string, answers: EstateAnswersBySection): void {
  mockAnswersStores[sessionId] = deepMergeAnswers(mockAnswersStores[sessionId] ?? {}, answers);
}

/** Replaces the in-memory saved answers document (e.g. after cornerstone invalidation). */
export function replaceMockAnswers(sessionId: string, answers: EstateAnswersBySection): void {
  const storeKey = mockSessionStores[sessionId] ? sessionId : MOCK_SESSION_ID;
  mockAnswersStores[storeKey] = answers;
}

/** Removes section ids from `progress.completedSectionIds` after cornerstone invalidation. */
export function removeMockCompletedSectionIds(sessionId: string, sectionIds: string[]): void {
  if (sectionIds.length === 0) return;
  const storeKey = mockSessionStores[sessionId] ? sessionId : MOCK_SESSION_ID;
  const session = mockSessionStores[storeKey];
  const completedSectionIds = session.progress?.completedSectionIds ?? [];
  const removeSet = new Set(sectionIds);
  const nextCompleted = completedSectionIds.filter((sectionId) => !removeSet.has(sectionId));
  const lastCompletedSectionId =
    session.progress?.lastCompletedSectionId && removeSet.has(session.progress.lastCompletedSectionId)
      ? nextCompleted[nextCompleted.length - 1] ?? null
      : session.progress?.lastCompletedSectionId ?? null;
  mockSessionStores[storeKey] = {
    ...session,
    progress: {
      lastCompletedSectionId,
      completedSectionIds: nextCompleted,
      questionnaireComplete: session.progress?.questionnaireComplete ?? false,
      questionnaireCompletedAt: session.progress?.questionnaireCompletedAt ?? null,
    },
  };
}

/** Applies a partial-upsert (deep merge) of `answers` and bumps `version`, mirroring §5.6 behavior. */
export function applyMockSaveProgress(
  sessionId: string,
  answers: EstateAnswersBySection,
  sectionName?: string,
  sectionStatus: 'IN_PROGRESS' | 'COMPLETED' = 'COMPLETED'
): EstateSession {
  const storeKey = mockSessionStores[sessionId] ? sessionId : MOCK_SESSION_ID;
  mockAnswersStores[storeKey] = deepMergeAnswers(mockAnswersStores[storeKey] ?? {}, answers);
  const session = mockSessionStores[storeKey];
  const nextVersion = session.version + 1;
  const nowIso = new Date().toISOString();
  const completedSectionIds = session.progress?.completedSectionIds ?? [];
  const shouldCompleteSection = sectionName && sectionStatus === 'COMPLETED';
  mockSessionStores[storeKey] = {
    ...session,
    version: nextVersion,
    updatedAt: nowIso,
    progress: {
      lastCompletedSectionId: shouldCompleteSection ? sectionName : session.progress?.lastCompletedSectionId ?? null,
      completedSectionIds:
        shouldCompleteSection && !completedSectionIds.includes(sectionName)
          ? [...completedSectionIds, sectionName]
          : completedSectionIds,
      questionnaireComplete: session.progress?.questionnaireComplete ?? false,
      questionnaireCompletedAt: session.progress?.questionnaireCompletedAt ?? null,
    },
  };
  return mockSessionStores[storeKey];
}

function deepMergeAnswers(target: EstateAnswersBySection, source: EstateAnswersBySection): EstateAnswersBySection {
  const result: EstateAnswersBySection = {...target};
  for (const [key, value] of Object.entries(source)) {
    const existing = result[key];
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      existing !== null &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
    ) {
      result[key] = deepMergeAnswers(existing as EstateAnswersBySection, value as EstateAnswersBySection);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Deletes every path present in `answered` from `source`, recursing into
 * nested objects but treating arrays/scalars as leaves (mirrors
 * `deepMergeAnswers`'s own object-vs-leaf split). Once a path has a saved
 * answer, the backend moves — doesn't duplicate — that suggestion into
 * `savedAnswers`, so it must no longer appear in `prefillAnswers`.
 */
function deepRemoveAnsweredPaths(
  source: Record<string, unknown>,
  answered: Record<string, unknown>
): Record<string, unknown> {
  const result = {...source};
  for (const [key, answeredValue] of Object.entries(answered)) {
    if (!(key in result)) continue;
    const existing = result[key];
    if (
      answeredValue !== null &&
      typeof answeredValue === 'object' &&
      !Array.isArray(answeredValue) &&
      existing !== null &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
    ) {
      result[key] = deepRemoveAnsweredPaths(
        existing as Record<string, unknown>,
        answeredValue as Record<string, unknown>
      );
    } else {
      delete result[key];
    }
  }
  return result;
}

/**
 * Strips every path already present in `savedAnswers` out of `prefillAnswers`
 * — mirrors the real backend: once an answer is saved (confirmed as-is or
 * edited), the corresponding prefill suggestion is removed, not left
 * duplicated alongside it. `people`/`packageType` are exempt: `people` is an
 * additive, append-only shared list (not a per-path suggestion to retire —
 * see `seedPrefillPeopleIntoAnswers`), and `packageType` is a prefill-only
 * key never mirrored into `savedAnswers` in the first place.
 */
function withoutAnsweredPrefillPaths(
  prefillAnswers: EstateAnswersBySection,
  savedAnswers: EstateAnswersBySection
): EstateAnswersBySection {
  const answeredPaths = {...savedAnswers};
  delete answeredPaths.people;
  delete answeredPaths.packageType;
  return deepRemoveAnsweredPaths(prefillAnswers, answeredPaths);
}

export const MOCK_SESSION_LIST: EstateSessionListItem[] = [
  {
    id: MOCK_SESSION_ID,
    status: 'ACTIVE',
    state: 'TX',
    packageType: 'WILL',
    questionnaireCatalogVersion: '2026.07.01',
    updatedAt: '2026-07-08T07:40:11Z',
  },
  {
    id: MOCK_SESSION_ID_TRUST,
    status: 'ACTIVE',
    state: 'CA',
    packageType: 'TRUST',
    questionnaireCatalogVersion: '2026.07.01',
    updatedAt: '2026-06-01T12:00:00Z',
  },
];

/** Composed Will flow template (replaces legacy monolithic mockQuestionnaireTemplate.ts). */
export const MOCK_QUESTIONNAIRE_TEMPLATE_WILL = getMockComposedFlowTemplate('WILL');

/** Composed Trust flow template. */
export const MOCK_QUESTIONNAIRE_TEMPLATE_TRUST = getMockComposedFlowTemplate('TRUST');

/** Per-session overrides merged into `prefillAnswers` (test-only). */
let mockPrefillAnswerPatches: Record<string, EstateAnswersBySection> = {};

export function patchMockPrefillAnswers(sessionId: string, patch: EstateAnswersBySection): void {
  mockPrefillAnswerPatches[sessionId] = deepMergeAnswers(mockPrefillAnswerPatches[sessionId] ?? {}, patch);
}

export function getMockPrefillResponse(sessionId: string): EstatePrefillResponse {
  const session = getMockSession(sessionId);
  const basePrefill = session.packageType === 'TRUST' ? buildTrustPrefillAnswers() : buildWillPrefillAnswers();
  const savedAnswers = getMockAnswers(sessionId);
  const prefillAnswers = withoutAnsweredPrefillPaths(
    deepMergeAnswers(basePrefill, mockPrefillAnswerPatches[sessionId] ?? {}),
    savedAnswers
  );
  return {
    id: sessionId,
    packageType: session.packageType,
    savedAnswers: {...savedAnswers},
    prefillAnswers,
    version: session.version,
  };
}

// ---------------------------------------------------------------------------
// GET /estate/sessions/{sessionId}/documents mock (DOCUMENT_GENERATION_LLD §4.2 / §7.2)
// ---------------------------------------------------------------------------

const MOCK_DOCUMENTS_UPDATED_AT = '2026-07-12T14:30:00Z';

/** Trust-package document list returned by `getMockDocuments` / `useEstateDocuments`. */
export const MOCK_DOCUMENTS_RESPONSE: EstateDocumentsResponse = {
  sessionId: MOCK_SESSION_ID,
  packageType: 'TRUST',
  status: 'COMPLETED',
  version: 3,
  updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
  documents: [
    {
      docType: 'last-will-and-testament',
      displayName: 'Last will & testament',
      format: 'PDF',
      fileName: 'Amelia rJpXcUMc - last-will-and-testament.pdf',
      filePath: 'estate-planning/trust-package/v3/Amelia rJpXcUMc/last-will-and-testament.pdf',
      nspNodeId: 'nsp-node-will-001',
      updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
      ownerName: 'Amelia rJpXcUMc',
      downloadUrl: 'https://example.com/mock/last-will-and-testament.pdf',
    },
    {
      docType: 'revocable-living-trust',
      displayName: 'Revocable living trust',
      format: 'PDF',
      fileName: 'Joint - revocable-living-trust.pdf',
      filePath: 'estate-planning/trust-package/v3/Joint/revocable-living-trust.pdf',
      nspNodeId: 'nsp-node-trust-001',
      updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
      ownerName: 'Joint',
      downloadUrl: 'https://example.com/mock/revocable-living-trust.pdf',
    },
    {
      docType: 'advance-health-directive',
      displayName: 'Advance health directive',
      format: 'PDF',
      fileName: 'Amelia rJpXcUMc - advance-health-directive.pdf',
      filePath: 'estate-planning/trust-package/v3/Amelia rJpXcUMc/advance-health-directive.pdf',
      nspNodeId: 'nsp-node-health-001',
      updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
      ownerName: 'Amelia rJpXcUMc',
      downloadUrl: 'https://example.com/mock/advance-health-directive.pdf',
    },
    {
      docType: 'power-of-attorney',
      displayName: 'Power of attorney',
      format: 'PDF',
      fileName: 'Spousey Mousey - power-of-attorney.pdf',
      filePath: 'estate-planning/trust-package/v3/Spousey Mousey/power-of-attorney.pdf',
      nspNodeId: 'nsp-node-poa-001',
      updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
      ownerName: 'Spousey Mousey',
      downloadUrl: 'https://example.com/mock/power-of-attorney.pdf',
    },
    {
      docType: 'pour-over-will',
      displayName: 'Pour-over will',
      format: 'PDF',
      fileName: 'Spousey Mousey - pour-over-will.pdf',
      filePath: 'estate-planning/trust-package/v3/Spousey Mousey/pour-over-will.pdf',
      nspNodeId: 'nsp-node-pour-over-001',
      updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
      ownerName: 'Spousey Mousey',
      downloadUrl: 'https://example.com/mock/pour-over-will.pdf',
    },
  ],
};

let mockDocumentsStore: EstateDocumentsResponse = {
  ...MOCK_DOCUMENTS_RESPONSE,
  documents: [...MOCK_DOCUMENTS_RESPONSE.documents],
};

/** Replaces the in-memory session document (local mock harness / tests). */
export function setMockSession(session: EstateSession): void {
  mockSessionStores[session.id] = {...session};
}

/** Marks notarization complete on the in-memory session (mock post-generation-steps). */
export function applyMockCompleteNotarization(sessionId: string, completedAt: string): EstateSession {
  const storeKey = mockSessionStores[sessionId] ? sessionId : MOCK_SESSION_ID;
  const session = mockSessionStores[storeKey];
  const nextVersion = session.version + 1;
  mockSessionStores[storeKey] = {
    ...session,
    status: 'DOCUMENT_GENERATION_COMPLETE',
    version: nextVersion,
    updatedAt: completedAt,
    postGenerationSteps: {
      notarization: {
        status: 'COMPLETED',
        completedAt,
      },
    },
  };
  return mockSessionStores[storeKey];
}

/** Replaces the in-memory documents payload (local mock harness / tests). */
export function setMockDocuments(documents: EstateDocumentsResponse): void {
  mockDocumentsStore = {
    ...documents,
    documents: [...documents.documents],
  };
}

/** Returns the in-memory mock documents payload for `useEstateDocuments`. */
export function getMockDocuments(): EstateDocumentsResponse {
  return {
    ...mockDocumentsStore,
    documents: [...mockDocumentsStore.documents],
  };
}

// ---------------------------------------------------------------------------
// GET /estate/sessions/{id}/prefill mock (ESTATE_SESSION_LLD §5.5)
// ---------------------------------------------------------------------------

/** Default Will-session prefill fixture. */
export const MOCK_PREFILL_RESPONSE: EstatePrefillResponse = getMockPrefillResponse(MOCK_SESSION_ID);

// ---------------------------------------------------------------------------
// Inventory Service mocks (ESTATE_INVENTORY_LLD.md)
// ---------------------------------------------------------------------------

export const MOCK_INVENTORY_ACCOUNT_ID = 'member-id';

/** Mock inventory prefill: cash and investment accounts only (per product expectation). */
const initialMockLineItems: InventoryLineItem[] = [
  {
    lineItemId: 'li-8a1b-4c2d',
    type: 'CASH_ACCOUNTS',
    itemOrigin: 'IMPORTED',
    importSource: 'FINANCIAL_MONITORING',
    enabled: true,
    details: {
      issuerName: 'Chase',
      last4: '4321',
      accountType: 'Checking',
      owner: 'owner_self',
    } as CashAccountDetails,
    externalCorrelationId: 'a3f2b9c8d1e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0',
    updatedAt: '2026-07-14T15:00:00Z',
  },
  {
    lineItemId: 'li-9d2e-5f3g',
    type: 'INVESTMENTS',
    itemOrigin: 'IMPORTED',
    importSource: 'FINANCIAL_MONITORING',
    enabled: true,
    details: {
      issuerName: 'Vanguard',
      last4: '5512',
      accountType: 'Retirement (401k, IRA)',
      owner: 'owner_self',
    } as CashAccountDetails,
    externalCorrelationId: 'b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c3',
    updatedAt: '2026-07-14T15:00:00Z',
  },
];

/** In-memory inventory "document" — mirrors §4.1 estate_inventory collection. */
let mockInventoryStore = {
  accountId: MOCK_INVENTORY_ACCOUNT_ID,
  version: 12,
  updatedAt: '2026-07-08T07:20:00Z',
  lastImportSyncAt: '2026-07-14T15:00:00Z' as string | null,
  lineItems: [...initialMockLineItems],
};

function buildLineItemsSummary(lineItems: InventoryLineItem[]) {
  return {
    lineItemCount: lineItems.length,
    enabledLineItemCount: lineItems.filter((item) => item.enabled).length,
    disabledImportedCount: lineItems.filter((item) => item.itemOrigin === 'IMPORTED' && !item.enabled).length,
  };
}

export function getMockInventoryLineItemsResponse(): InventoryLineItemsResponse {
  return {
    accountId: mockInventoryStore.accountId,
    version: mockInventoryStore.version,
    updatedAt: mockInventoryStore.updatedAt,
    lineItems: mockInventoryStore.lineItems,
    summary: buildLineItemsSummary(mockInventoryStore.lineItems),
  };
}

function bumpMockInventoryVersion(): number {
  mockInventoryStore = {
    ...mockInventoryStore,
    version: mockInventoryStore.version + 1,
    updatedAt: new Date().toISOString(),
  };
  return mockInventoryStore.version;
}

/** Mirrors §5.3 create behavior: assigns a new lineItemId, MANUAL origin, bumps version. */
export function createMockLineItem(
  type: InventoryLineItem['type'],
  details: InventoryLineItem['details']
): InventoryLineItem {
  const newItem: InventoryLineItem = {
    lineItemId: createEstateEntityId(),
    type,
    itemOrigin: 'MANUAL',
    importSource: 'MANUAL',
    enabled: true,
    details,
    updatedAt: new Date().toISOString(),
  };
  mockInventoryStore = {...mockInventoryStore, lineItems: [...mockInventoryStore.lineItems, newItem]};
  bumpMockInventoryVersion();
  return newItem;
}

/** Mirrors §5.4 unified edit behavior. Throws when a patch is sent for an IMPORTED item. */
export function editMockLineItem(
  lineItemId: string,
  patch: {details?: Record<string, unknown>} | undefined,
  enabled: boolean | undefined
): InventoryLineItem {
  const existing = mockInventoryStore.lineItems.find((item) => item.lineItemId === lineItemId);
  if (!existing) {
    throw new Error(`Line item not found: ${lineItemId}`);
  }
  if (patch?.details && existing.itemOrigin === 'IMPORTED') {
    throw new Error('IMPORT_ITEM_IMMUTABLE');
  }
  const updated: InventoryLineItem = {
    ...existing,
    details: patch?.details
      ? ({...existing.details, ...patch.details} as InventoryLineItem['details'])
      : existing.details,
    enabled: enabled ?? existing.enabled,
    updatedAt: new Date().toISOString(),
  };
  mockInventoryStore = {
    ...mockInventoryStore,
    lineItems: mockInventoryStore.lineItems.map((item) => (item.lineItemId === lineItemId ? updated : item)),
  };
  bumpMockInventoryVersion();
  return updated;
}

/** Mirrors §5.5 delete behavior: hard-removes the item and bumps version. */
export function deleteMockLineItem(lineItemId: string): void {
  mockInventoryStore = {
    ...mockInventoryStore,
    lineItems: mockInventoryStore.lineItems.filter((item) => item.lineItemId !== lineItemId),
  };
  bumpMockInventoryVersion();
}

/**
 * Mirrors §5.2 import-sync behavior in miniature: "discovers" one new imported
 * real-estate record on every call so the UI has something to show, skipping
 * anything already present (by lineItemId, standing in for externalCorrelationId dedup).
 */
export function runMockImportSync(): {
  importedCount: number;
  skippedCount: number;
  version: number;
  lastImportSyncAt: string;
} {
  const candidateId = 'li-imported-sync-1';
  const alreadyPresent = mockInventoryStore.lineItems.some((item) => item.lineItemId === candidateId);
  let importedCount = 0;
  let skippedCount = 0;
  if (!alreadyPresent) {
    const newItem: InventoryLineItem = {
      lineItemId: candidateId,
      type: 'INVESTMENTS',
      itemOrigin: 'IMPORTED',
      importSource: 'FINANCIAL_MONITORING',
      enabled: false,
      details: {
        issuerName: 'Fidelity',
        last4: '7788',
        accountType: 'Brokerage',
        owner: 'owner_self',
      },
      externalCorrelationId: 'f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7',
      updatedAt: new Date().toISOString(),
    };
    mockInventoryStore = {...mockInventoryStore, lineItems: [...mockInventoryStore.lineItems, newItem]};
    importedCount = 1;
  } else {
    skippedCount = 1;
  }
  const lastImportSyncAt = new Date().toISOString();
  mockInventoryStore = {...mockInventoryStore, lastImportSyncAt};
  const version = bumpMockInventoryVersion();
  return {importedCount, skippedCount, version, lastImportSyncAt};
}

/** Test-only: restores all mock stores to their initial state between test files. */
export function resetEstatePlanningMockData(): void {
  mockSessionStores = {
    [MOCK_SESSION_ID]: {...initialWillSession},
    [MOCK_SESSION_ID_TRUST]: {...initialTrustSession},
  };
  mockAnswersStores = {
    [MOCK_SESSION_ID]: {},
    [MOCK_SESSION_ID_TRUST]: {},
  };
  mockPrefillAnswerPatches = {};
  mockInventoryStore = {
    accountId: MOCK_INVENTORY_ACCOUNT_ID,
    version: 12,
    updatedAt: '2026-07-08T07:20:00Z',
    lastImportSyncAt: '2026-07-14T15:00:00Z',
    lineItems: [...initialMockLineItems],
  };
  mockDocumentsStore = {
    ...MOCK_DOCUMENTS_RESPONSE,
    documents: [...MOCK_DOCUMENTS_RESPONSE.documents],
  };
}
